import { FormEvent, useState } from "react";
import { EmptyState, ErrorState, FreshnessBar, LoadingState, formatDate } from "../components/AdminPageStates";
import MetricCard from "../components/MetricCard";
import { useShoutOutsAdmin } from "../hooks/useShoutOutsAdmin";
import { SHOUT_OUT_REASONS } from "../services/shoutOutsAdminService";

type RankingView = "top" | "zero" | "recent";

function preview(value: string | null) {
  if (!value) {
    return "Unavailable";
  }

  return value.length > 90 ? `${value.slice(0, 90)}...` : value;
}

export default function ShoutOuts() {
  const { data, error, filters, isLoading, refresh, setFilters } = useShoutOutsAdmin();
  const [rankingView, setRankingView] = useState<RankingView>("top");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void refresh(filters);
  }

  if (isLoading && !data) {
    return <LoadingState message="Loading shout-outs." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No shout-out data is available." />;
  }

  return (
    <>
      <FreshnessBar
        dataSource="peerShoutOuts, users"
        isLive={false}
        lastUpdated={data.lastUpdated}
        onRefresh={() => {
          void refresh(filters);
        }}
      />

      <form className="filter-panel" onSubmit={handleSubmit}>
        <label>
          Date range start
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilters({ ...filters, startDate: event.target.value })}
          />
        </label>
        <label>
          Date range end
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilters({ ...filters, endDate: event.target.value })}
          />
        </label>
        <label>
          Reason
          <select value={filters.reason} onChange={(event) => setFilters({ ...filters, reason: event.target.value })}>
            <option value="">All reasons</option>
            {SHOUT_OUT_REASONS.map((reason) => (
              <option value={reason} key={reason}>
                {reason}
              </option>
            ))}
          </select>
        </label>
        <label>
          Recipient role
          <input
            type="text"
            placeholder="coach, dancer, parent"
            value={filters.recipientRole}
            onChange={(event) => setFilters({ ...filters, recipientRole: event.target.value })}
          />
        </label>
        <label>
          Giver role
          <input
            type="text"
            placeholder="coach, dancer, parent"
            value={filters.giverRole}
            onChange={(event) => setFilters({ ...filters, giverRole: event.target.value })}
          />
        </label>
        <button type="submit" className="primary-button">
          Apply
        </button>
      </form>

      <div className="overview-grid">
        <MetricCard title="Total shout-outs" value={data.totalShoutOuts?.toString() ?? "Unavailable"} note="Server count from peerShoutOuts." />
        <MetricCard title="Shout-outs today" value={data.shoutOutsToday?.toString() ?? "Unavailable"} note="Local admin day count." />
        <MetricCard
          title="Users with most received"
          value={data.topRecipients[0]?.name ?? "Unavailable"}
          note={data.topRecipients[0] ? `${data.topRecipients[0].count} in current bounded result set.` : "No ranked recipients available."}
        />
        <MetricCard
          title="Users with no shout-outs"
          value="Unavailable"
          note={data.zeroShoutOutsUnavailableReason}
          state="unavailable"
        />
        <MetricCard
          title="Most common reason"
          value={data.commonReasons[0]?.reason ?? "Unavailable"}
          note={data.commonReasons[0] ? `${data.commonReasons[0].count} in current bounded result set.` : "No reason summary available."}
        />
      </div>

      <div className="segmented-control">
        <button type="button" className={rankingView === "top" ? "active" : ""} onClick={() => setRankingView("top")}>
          Top shouted-out users
        </button>
        <button type="button" className={rankingView === "zero" ? "active" : ""} onClick={() => setRankingView("zero")}>
          Users with zero shout-outs
        </button>
        <button type="button" className={rankingView === "recent" ? "active" : ""} onClick={() => setRankingView("recent")}>
          Recent shout-outs
        </button>
      </div>

      {rankingView === "top" ? (
        data.topRecipients.length > 0 ? (
          <div className="admin-list">
            {data.topRecipients.map((recipient) => (
              <section className="admin-list-row" key={recipient.userId}>
                <div>
                  <p className="row-title">{recipient.name}</p>
                  <p className="row-meta">{recipient.role}</p>
                </div>
                <p className="row-meta">{recipient.count} shout-outs</p>
              </section>
            ))}
          </div>
        ) : (
          <EmptyState message="No top recipient data is available for the current filters." />
        )
      ) : null}

      {rankingView === "zero" ? <EmptyState message={data.zeroShoutOutsUnavailableReason} /> : null}

      {rankingView === "recent" ? (
        data.filteredShoutOuts.length > 0 ? (
          <div className="admin-list">
            {data.filteredShoutOuts.slice(0, 12).map((shoutOut) => (
              <section className="admin-list-row" key={shoutOut.id}>
                <div>
                  <p className="row-title">{shoutOut.recipientName}</p>
                  <p className="row-meta">
                    From {shoutOut.giverName} / {shoutOut.topic ?? "Unavailable"}
                  </p>
                </div>
                <p className="row-meta">{formatDate(shoutOut.createdAt)}</p>
              </section>
            ))}
          </div>
        ) : (
          <EmptyState message="No recent shout-outs match the current filters." />
        )
      ) : null}

      {data.commonReasons.length > 0 ? (
        <div className="admin-list">
          {data.commonReasons.map((reason) => (
            <section className="admin-list-row" key={reason.reason}>
              <p className="row-title">{reason.reason}</p>
              <p className="row-meta">{reason.count} shout-outs</p>
            </section>
          ))}
        </div>
      ) : null}

      <div className="table-shell">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Recipient</th>
              <th>Giver</th>
              <th>Reason</th>
              <th>Comment preview</th>
              <th>Created</th>
              <th>Visibility/status</th>
            </tr>
          </thead>
          <tbody>
            {data.filteredShoutOuts.map((shoutOut) => (
              <tr key={shoutOut.id}>
                <td>
                  <strong>{shoutOut.recipientName}</strong>
                  <span>{shoutOut.recipientRole}</span>
                </td>
                <td>
                  <strong>{shoutOut.giverName}</strong>
                  <span>{shoutOut.giverRole}</span>
                </td>
                <td>{shoutOut.topic ?? "Unavailable"}</td>
                <td>{preview(shoutOut.privateNote)}</td>
                <td>{formatDate(shoutOut.createdAt)}</td>
                <td>{shoutOut.visibility ?? "Unavailable"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
